import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, useDataProvider, useLocaleState, useNotify, useTranslate } from "react-admin";

import {
  GetIntegrationImportPreviewQuery,
  IntegrationExecutionStatus,
  IntegrationPreviewChange,
  LocationTypeEnum,
} from "../generated/graphql";
import { formatDateTimeInline } from "../lib/dateUtils";
import { space } from "../theme/designTokens";

const POLL_INTERVAL_MS = 2000;

type PreviewData = NonNullable<GetIntegrationImportPreviewQuery["integrationImportPreview"]>;
type PreviewLocation = PreviewData["locations"][number];
type PreviewPerson = PreviewData["patients"][number];
type PreviewIssue = PreviewData["failures"][number];
type LocationRef = { type: LocationTypeEnum; externalId: string };

const CHANGES = [
  IntegrationPreviewChange.New,
  IntegrationPreviewChange.Changed,
  IntegrationPreviewChange.Unchanged,
  IntegrationPreviewChange.Failed,
];

const changeColors: Record<IntegrationPreviewChange, "success" | "warning" | "default" | "error"> = {
  [IntegrationPreviewChange.New]: "success",
  [IntegrationPreviewChange.Changed]: "warning",
  [IntegrationPreviewChange.Unchanged]: "default",
  [IntegrationPreviewChange.Failed]: "error",
};

/**
 * Backend reasons that have a translation. Any other reason is provider text
 * and is shown as it came.
 */
const knownReasonKeys: Record<string, string> = {
  "Missing patient name": "missingPatientName",
  "Invalid birth date": "invalidBirthDate",
  "Organization has no root location": "noRootLocation",
};

const isExpired = (preview: PreviewData) => new Date(preview.expiresAt).getTime() <= Date.now();

const knownEntities = ["location", "patient", "practitioner", "procedure"];

const locationKey = (ref: LocationRef) => `${ref.type}:${ref.externalId}`;

type TreeNode = {
  key: string;
  location: PreviewLocation;
  parent: TreeNode | null;
  children: TreeNode[];
  residents: number;
};

/**
 * Builds the location tree from the parent references. A location whose parent
 * is missing or unknown is a root. A resident counts once at the location they
 * are assigned to and once at every ancestor of it.
 */
const buildTree = (preview: PreviewData): { roots: TreeNode[]; unassigned: number } => {
  const nodes = new Map<string, TreeNode>();
  preview.locations.forEach(location => {
    const key = locationKey(location);
    if (!nodes.has(key)) {
      nodes.set(key, { key, location, parent: null, children: [], residents: 0 });
    }
  });

  const roots: TreeNode[] = [];
  nodes.forEach(node => {
    const parent = node.location.parent ? nodes.get(locationKey(node.location.parent)) : undefined;
    if (parent && parent !== node) {
      node.parent = parent;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const residentsByNode = new Map<string, Set<number>>();
  preview.patients.forEach((patient, index) => {
    patient.locations.forEach(ref => {
      const visited = new Set<string>();
      let current = nodes.get(locationKey(ref));
      while (current && !visited.has(current.key)) {
        visited.add(current.key);
        const residents = residentsByNode.get(current.key) ?? new Set<number>();
        residents.add(index);
        residentsByNode.set(current.key, residents);
        current = current.parent ?? undefined;
      }
    });
  });
  nodes.forEach(node => {
    node.residents = residentsByNode.get(node.key)?.size ?? 0;
  });

  return { roots, unassigned: preview.patients.filter(patient => patient.locations.length === 0).length };
};

const countByChange = (items: { change: IntegrationPreviewChange }[]) =>
  CHANGES.map(change => items.filter(item => item.change === change).length);

const personReference = (person: PreviewPerson) => person.number || person.externalId;

/**
 * Failed people carry their reason on the person. Add them to the failures
 * unless the backend already listed the same entity and reference.
 */
const collectFailures = (preview: PreviewData): PreviewIssue[] => {
  const seen = new Set(preview.failures.map(issue => `${issue.entity}\u0000${issue.reference ?? ""}`));
  const failures = [...preview.failures];
  const addFailedPeople = (entity: string, people: PreviewPerson[]) =>
    people.forEach(person => {
      if (person.change !== IntegrationPreviewChange.Failed || !person.reason) return;
      const reference = personReference(person);
      const id = `${entity}\u0000${reference}`;
      if (seen.has(id)) return;
      seen.add(id);
      failures.push({ entity, reference, reason: person.reason });
    });
  addFailedPeople("patient", preview.patients);
  addFailedPeople("practitioner", preview.practitioners);
  return failures;
};

const ChangeChip = ({ change }: { change: IntegrationPreviewChange }) => {
  const translate = useTranslate();
  return (
    <Chip
      size="small"
      variant="outlined"
      color={changeColors[change]}
      label={translate(`integration.preview.change.${change}`)}
    />
  );
};

const LocationTreeNode = ({ node }: { node: TreeNode }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;

  const content = (
    <>
      <ListItemText
        primary={node.location.name}
        secondary={translate(`integration.preview.locationType.${node.location.type}`)}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: space.xs }}>
        <ChangeChip change={node.location.change} />
        <Typography variant="body2" color="text.secondary">
          {translate("integration.preview.tree.residents", { smart_count: node.residents })}
        </Typography>
        {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
      </Box>
    </>
  );

  return (
    <>
      <ListItem disablePadding>
        {hasChildren ? (
          <ListItemButton onClick={() => setOpen(prev => !prev)} aria-expanded={open}>
            {content}
          </ListItemButton>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", px: space.xs, py: space.xxs }}>
            {content}
          </Box>
        )}
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: space.xs }}>
            {node.children.map(child => (
              <LocationTreeNode key={child.key} node={child} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const IssueList = ({ title, issues }: { title: string; issues: PreviewIssue[] }) => {
  const translate = useTranslate();
  if (issues.length === 0) return null;
  return (
    <Box sx={{ mt: space.sm }}>
      <Typography variant="subtitle2">{title}</Typography>
      <List dense disablePadding>
        {issues.map((issue, index) => {
          const reasonKey = knownReasonKeys[issue.reason];
          const entity = knownEntities.includes(issue.entity)
            ? translate(`integration.preview.entity.${issue.entity}`)
            : issue.entity;
          return (
            <ListItem key={`${issue.entity}:${issue.reference ?? ""}:${index}`} disableGutters>
              <ListItemText
                primary={reasonKey ? translate(`integration.preview.reason.${reasonKey}`) : issue.reason}
                secondary={
                  issue.reference
                    ? translate("integration.preview.issueReference", { entity, reference: issue.reference })
                    : entity
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

const PreviewContent = ({ preview }: { preview: PreviewData }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const tree = useMemo(() => buildTree(preview), [preview]);
  const failures = useMemo(() => collectFailures(preview), [preview]);

  const totals = [
    { key: "locations", counts: countByChange(preview.locations) },
    { key: "patients", counts: countByChange(preview.patients) },
    { key: "practitioners", counts: countByChange(preview.practitioners) },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {translate("integration.preview.createdAt", { date: formatDateTimeInline(preview.createdAt, locale) })}
        {" · "}
        {translate("integration.preview.expiresAt", { date: formatDateTimeInline(preview.expiresAt, locale) })}
      </Typography>

      <Table size="small" sx={{ mt: space.sm }}>
        <TableHead>
          <TableRow>
            <TableCell>{translate("integration.preview.totals.title")}</TableCell>
            {CHANGES.map(change => (
              <TableCell key={change} align="right">
                {translate(`integration.preview.change.${change}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {totals.map(row => (
            <TableRow key={row.key}>
              <TableCell component="th" scope="row">
                {translate(`integration.preview.totals.${row.key}`)}
              </TableCell>
              {row.counts.map((count, index) => (
                <TableCell key={CHANGES[index]} align="right">
                  {count}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="body2" color="text.secondary" mt={space.xs}>
        {translate("integration.preview.procedureCount", { smart_count: preview.procedureCount })}
      </Typography>

      <Typography variant="subtitle2" mt={space.sm}>
        {translate("integration.preview.tree.title")}
      </Typography>
      {tree.roots.length > 0 ? (
        <List disablePadding sx={{ maxHeight: 400, overflow: "auto" }}>
          {tree.roots.map(root => (
            <LocationTreeNode key={root.key} node={root} />
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate("integration.preview.tree.empty")}
        </Typography>
      )}
      <Typography variant="body2" mt={space.xs}>
        {translate("integration.preview.tree.unassigned", { smart_count: tree.unassigned })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {translate("integration.preview.tree.unassignedHint")}
      </Typography>

      <IssueList title={translate("integration.preview.warnings")} issues={preview.warnings} />
      <IssueList title={translate("integration.preview.failures")} issues={failures} />
    </Box>
  );
};

type IntegrationImportPreviewProps = {
  organizationId: string;
  /** The preview and the import run with the saved settings, so unsaved edits block them. */
  disabled: boolean;
  onImport: () => void;
  importing: boolean;
};

export const IntegrationImportPreview = ({
  organizationId,
  disabled,
  onImport,
  importing,
}: IntegrationImportPreviewProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<IntegrationExecutionStatus | null>(null);
  // Invalidates responses and poll timers of a previous organization or unmounted view.
  const generation = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loadPreview = useCallback(
    async (run: number) => {
      try {
        const loaded: PreviewData | null = await dataProvider.getIntegrationImportPreview(organizationId);
        if (run === generation.current) {
          setPreview(loaded && !isExpired(loaded) ? loaded : null);
        }
      } catch {
        if (run === generation.current) {
          notify("integration.preview.error.loading", { type: "error" });
        }
      }
    },
    [dataProvider, notify, organizationId]
  );

  useEffect(() => {
    generation.current += 1;
    const run = generation.current;
    setPreview(null);
    setRunning(false);
    setFailure(null);
    setLoading(true);
    loadPreview(run).finally(() => {
      if (run === generation.current) {
        setLoading(false);
      }
    });
    return () => {
      generation.current += 1;
      clearTimeout(pollTimer.current);
    };
  }, [loadPreview]);

  // The backend stops returning the preview when it expires, so stop offering it as well.
  useEffect(() => {
    if (!preview) return undefined;
    const timer = setTimeout(() => setPreview(null), Math.max(new Date(preview.expiresAt).getTime() - Date.now(), 0));
    return () => clearTimeout(timer);
  }, [preview]);

  const finishRun = useCallback(
    async (run: number, status: IntegrationExecutionStatus) => {
      if (status === IntegrationExecutionStatus.Completed) {
        await loadPreview(run);
      } else if (run === generation.current) {
        setFailure(status);
      }
      if (run === generation.current) {
        setRunning(false);
      }
    },
    [loadPreview]
  );

  const pollExecution = useCallback(
    (run: number, executionId: string) => {
      pollTimer.current = setTimeout(async () => {
        try {
          const execution: { status: IntegrationExecutionStatus } = await dataProvider.getIntegrationExecution(
            organizationId,
            executionId
          );
          if (run !== generation.current) return;
          if (
            execution.status === IntegrationExecutionStatus.Pending ||
            execution.status === IntegrationExecutionStatus.Running
          ) {
            pollExecution(run, executionId);
          } else {
            await finishRun(run, execution.status);
          }
        } catch {
          if (run === generation.current) {
            setRunning(false);
            notify("integration.preview.error.request", { type: "error" });
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [dataProvider, finishRun, notify, organizationId]
  );

  const handleCreate = async () => {
    const run = generation.current;
    setFailure(null);
    setRunning(true);
    try {
      const execution: { id: string; status: IntegrationExecutionStatus } =
        await dataProvider.previewIntegrationImport(organizationId);
      if (run !== generation.current) return;
      if (
        execution.status === IntegrationExecutionStatus.Pending ||
        execution.status === IntegrationExecutionStatus.Running
      ) {
        pollExecution(run, execution.id);
      } else {
        await finishRun(run, execution.status);
      }
    } catch {
      if (run === generation.current) {
        setRunning(false);
        notify("integration.preview.error.request", { type: "error" });
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 600, margin: "auto", mt: space.sm }}>
      <Typography variant="h6">{translate("integration.preview.title")}</Typography>
      <Typography variant="body2" color="text.secondary" mb={space.sm}>
        {translate("integration.preview.description")}
      </Typography>
      <Button
        color="secondary"
        variant="outlined"
        onClick={handleCreate}
        disabled={disabled || running || loading}
        label={running ? "integration.preview.button.creating" : "integration.preview.button.create"}
      />
      {disabled && (
        <Typography variant="caption" color="text.secondary" display="block" mt={space.xs}>
          {translate("integration.preview.saveFirst")}
        </Typography>
      )}
      {running && (
        <Box sx={{ mt: space.sm }} role="status">
          <Typography variant="body2">{translate("integration.preview.running")}</Typography>
          <LinearProgress sx={{ mt: space.xs }} />
        </Box>
      )}
      {failure && (
        <Alert severity="error" sx={{ mt: space.sm }}>
          {translate(
            failure === IntegrationExecutionStatus.Cancelled
              ? "integration.preview.error.cancelled"
              : "integration.preview.error.failed"
          )}
        </Alert>
      )}
      <Box sx={{ mt: space.sm }}>
        {loading && <Typography variant="body2">{translate("integration.preview.loading")}</Typography>}
        {!loading && !preview && !running && (
          <Typography variant="body2" color="text.secondary">
            {translate("integration.preview.empty")}
          </Typography>
        )}
        {!loading && preview && (
          <>
            <PreviewContent preview={preview} />
            <Alert severity="info" sx={{ mt: space.sm }}>
              {translate("integration.preview.importNotice")}
            </Alert>
            <Box sx={{ mt: space.xs }}>
              <Button
                color="primary"
                variant="contained"
                onClick={onImport}
                disabled={disabled || importing || running}
                label="integration.preview.button.import"
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
